import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ShieldX } from 'lucide-react';

interface PermissionContextType {
  showPermissionDenied: (customMessage?: string) => void;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const showPermissionDenied = useCallback((customMessage?: string) => {
    setMessage(customMessage || null);
    setIsOpen(true);
  }, []);

  return (
    <PermissionContext.Provider value={{ showPermissionDenied }}>
      {children}
      
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <ShieldX className="h-8 w-8 text-destructive" />
            </div>
            <AlertDialogTitle className="text-xl">
              Acesso Negado
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {message || 'Você não tem permissão para acessar esta funcionalidade.'}
              <br />
              <span className="mt-2 block text-sm text-muted-foreground">
                Entre em contato com o Administrador do sistema para solicitar acesso.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center">
            <AlertDialogAction className="min-w-[120px]">
              Fechar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PermissionContext.Provider>
  );
}

export function usePermission() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
}
